import type { ChatContextPickingControl } from '../blocksuite/ai/components/ai-chat-content/type';
import { AffineCommandRegistry, PreconditionStrategy } from './registry';

export const DEFAULT_SELECTOR_SHORTCUT = '$mod+Alt+KeyS';
const selectorCommandPrefix = 'copilot:context-selector:';
const modifierAliases: Record<string, string> = {
  $mod: '$mod',
  ctrl: 'Control',
  control: 'Control',
  meta: 'Meta',
  cmd: 'Meta',
  command: 'Meta',
  '⌘': 'Meta',
  alt: 'Alt',
  option: 'Alt',
  '⌥': 'Alt',
  shift: 'Shift',
  '⇧': 'Shift',
  $shift: 'Shift',
  $alt: 'Alt',
};

/**
 * One Tinykeys press, not a second keybinding grammar or global listener. A binding that types
 * text where it applies needs a modifier; one that only applies where nothing is typed may be a
 * bare key (`bareKey`), Tab included.
 */
export function normalizeSelectorShortcut(
  value: string,
  bareKey = false
): string | null {
  if (value.length > 80) return null;
  const parts = value
    .trim()
    .split('+')
    .map(part => part.trim());
  const key = parts.pop();
  if (!key || (!parts.length && !bareKey)) return null;
  const modifiers = parts.map(part => modifierAliases[part.toLowerCase()]);
  if (
    modifiers.some(part => !part) ||
    new Set(modifiers).size !== modifiers.length
  )
    return null;
  if (
    modifiers.includes('$mod') &&
    (modifiers.includes('Control') || modifiers.includes('Meta'))
  )
    return null;
  let code: string;
  if (/^(?:Key)?[a-z]$/i.test(key)) code = `Key${key.slice(-1).toUpperCase()}`;
  else if (/^(?:Digit)?[0-9]$/i.test(key)) code = `Digit${key.slice(-1)}`;
  else if (/^F(?:[1-9]|1[0-2])$/i.test(key)) code = key.toUpperCase();
  else if (bareKey && /^tab$/i.test(key)) code = 'Tab';
  else return null;
  return ['$mod', 'Control', 'Meta', 'Alt', 'Shift']
    .filter(part => modifiers.includes(part))
    .concat(code)
    .join('+');
}

function comparableBinding(value: string): string | null {
  return (
    normalizeSelectorShortcut(value, true)?.replace(
      '$mod',
      environment.isMacOs ? 'Meta' : 'Control'
    ) ?? null
  );
}

export function selectorShortcutConflicts(
  value: string,
  nativeBindings: Iterable<string> = [],
  ownCommandPrefix = selectorCommandPrefix
) {
  const binding = comparableBinding(value);
  if (binding === null) return false;
  for (const native of nativeBindings)
    if (comparableBinding(native) === binding) return true;
  // Iterate existing command identities, including their native skipRegister bindings.
  for (const command of AffineCommandRegistry.commands.values()) {
    if (
      !command.id.startsWith(ownCommandPrefix) &&
      command.keyBinding &&
      comparableBinding(command.keyBinding.binding) === binding
    )
      return true;
  }
  return false;
}

export function formatSelectorShortcut(binding: string): string {
  const keys: Record<string, string> = {
    $mod: environment.isMacOs ? '⌘' : 'Ctrl',
    Control: 'Ctrl',
    Meta: environment.isMacOs ? '⌘' : 'Meta',
    Alt: environment.isMacOs ? '⌥' : 'Alt',
    Shift: environment.isMacOs ? '⇧' : 'Shift',
  };
  return binding
    .split('+')
    .map(part => keys[part] ?? part.replace(/^(Key|Digit)/, ''))
    .join(' + ');
}

export function captureSelectorShortcut(
  event: Pick<
    KeyboardEvent,
    'key' | 'code' | 'ctrlKey' | 'metaKey' | 'altKey' | 'shiftKey'
  >,
  bareKey = false
): string | null {
  if (['Control', 'Meta', 'Alt', 'Shift'].includes(event.key)) return null;
  return normalizeSelectorShortcut(
    [
      ...(event.ctrlKey ? ['Control'] : []),
      ...(event.metaKey ? ['Meta'] : []),
      ...(event.altKey ? ['Alt'] : []),
      ...(event.shiftKey ? ['Shift'] : []),
      event.code || event.key,
    ].join('+'),
    bareKey
  );
}

/** Check the bounded event path; no DOM enumeration, snapshot, or layout read. */
export function editableEvent(event: KeyboardEvent): boolean {
  for (const target of event.composedPath().slice(0, 64)) {
    if (!(target instanceof HTMLElement)) continue;
    if (
      ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) ||
      target.isContentEditable ||
      target.getAttribute('role') === 'textbox'
    )
      return true;
    const editable = target.getAttribute('contenteditable');
    if (editable !== null && editable !== 'false') return true;
  }
  return false;
}

function visibleInput(input: HTMLElement): boolean {
  if (!input.isConnected) return false;
  let node: HTMLElement | null = input;
  for (let depth = 0; node && depth < 64; depth++) {
    if (
      node.hidden ||
      node.inert ||
      node.getAttribute('aria-hidden') === 'true'
    )
      return false;
    const root = node.getRootNode();
    node =
      node.parentElement ??
      (root instanceof ShadowRoot ? (root.host as HTMLElement) : null);
  }
  return node === null;
}

/** The actual Input lifecycle registers its existing scoped toggle once. */
export function bindContextSelectorShortcut(
  control: ChatContextPickingControl,
  input: HTMLElement,
  label: () => string,
  changed: (binding: string | null) => void
): () => void {
  const source = control.shortcut;
  if (!source) {
    changed(null);
    return () => {};
  }
  const commandId =
    selectorCommandPrefix +
    JSON.stringify([control.scope.workspaceId, control.scope.viewId]);
  let release: (() => void) | undefined;
  let desired: string | null = source.getBinding();
  let active: string | null | undefined;
  // Command-modifier chords never type text, so they also work in the chat input/editor.
  const typesNoText = (binding: string) =>
    binding
      .split('+')
      .some(part => part === '$mod' || part === 'Control' || part === 'Meta');
  const sync = () => {
    const binding =
      desired && !selectorShortcutConflicts(desired) ? desired : null;
    if (binding === active) return;
    active = binding;
    AffineCommandRegistry.batch(() => {
      release?.();
      release = undefined;
      changed(binding);
      if (!binding) return;
      release = AffineCommandRegistry.register({
        id: commandId,
        label,
        icon: null,
        preconditionStrategy: PreconditionStrategy.Never,
        keyBinding: {
          binding,
          admit: event =>
            !event.defaultPrevented &&
            !event.repeat &&
            !event.isComposing &&
            event.keyCode !== 229 &&
            (typesNoText(binding) || !editableEvent(event)) &&
            visibleInput(input) &&
            control.canToggle(),
        },
        run: () => {
          control.setEnabled(!control.isEnabled());
        },
      });
    });
  };
  const unsubscribe = source.subscribe(binding => {
    desired = binding;
    sync();
  });
  sync();
  const commandsSubscription = AffineCommandRegistry.changes$.subscribe(sync);
  return () => {
    commandsSubscription.unsubscribe();
    unsubscribe();
    release?.();
  };
}
